/**
 * Admin Settings Page
 *
 * Hub for admin settings with cards for:
 * - Event Types (templates for events)
 * - Organization Settings
 * - Notification Preferences
 * - User Permissions
 * - Integrations
 */

import { Link } from 'react-router-dom';
import { useOrganization } from '../../context/OrganizationContext';
import InstallPrompt from '../../components/pwa/InstallPrompt';
import EnablePushCard from '../../components/pwa/EnablePushCard';

export default function AdminSettings() {
  const { currentOrg } = useOrganization();
  const isOwner = currentOrg?.role === 'OWNER';

  return (
    <div>
      {/* Page header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-600 mt-1">
          Manage settings for {currentOrg?.orgName || 'your organization'}
        </p>
      </div>

      {/* PWA install + notifications cards (self-hide when not applicable) */}
      <div className="mb-6 space-y-3">
        <InstallPrompt />
        <EnablePushCard />
      </div>

      {/* Settings cards grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <SettingsCard
          icon={<TemplateIcon className="w-6 h-6" />}
          title="Event Types"
          description="Create templates with predefined roles for quick event setup"
          to="/admin/settings/event-types"
        />
        <SettingsCard
          icon={<BuildingIcon className="w-6 h-6" />}
          title="Organization Settings"
          description="Update organization name, branding, and general information"
          to="/admin/settings/organization"
        />
        {isOwner && (
          <SettingsCard
            icon={<CreditCardIcon className="w-6 h-6" />}
            title="Billing & Subscription"
            description="Manage your subscription plan and view usage"
            to="/admin/settings/billing"
          />
        )}
        <SettingsCard
          icon={<BellIcon className="w-6 h-6" />}
          title="Notification Settings"
          description="Organization-wide notification controls, reminders, and digests"
          to="/admin/settings/notifications"
        />
        <SettingsCard
          icon={<ShieldIcon className="w-6 h-6" />}
          title="User Permissions"
          description="Manage roles and access control for team members"
        />
        <SettingsCard
          icon={<PlugIcon className="w-6 h-6" />}
          title="Integrations"
          description="Let crew subscribe to the schedule from Google, Apple or Outlook"
          to="/admin/settings/integrations"
        />
      </div>

      {/* Owner notice */}
      {isOwner && (
        <div className="bg-surface-panel border border-hairline border-l-[3px] border-l-rack p-4">
          <div className="flex items-start">
            <InfoIcon className="w-5 h-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-blue-800">Organization Owner</p>
              <p className="text-sm text-blue-600 mt-1">
                As the organization owner, you have full access to all settings including
                billing, subscription management, and the ability to transfer ownership.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Sub-components
// =============================================================================

interface SettingsCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  to?: string;
}

function SettingsCard({ icon, title, description, to }: SettingsCardProps) {
  const content = (
    <div className="flex items-start">
      <div className="flex-shrink-0">
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${to ? 'bg-primary-100 text-primary-600' : 'bg-gray-100 text-gray-500'}`}>
          {icon}
        </div>
      </div>
      <div className="ml-4 flex-1">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-gray-900">{title}</h3>
          {!to && (
            <span className="px-2.5 py-0.5 text-xs font-medium bg-gray-100 text-gray-600">
              Coming Soon
            </span>
          )}
          {to && (
            <ChevronRightIcon className="w-5 h-5 text-gray-400" />
          )}
        </div>
        <p className="text-sm text-gray-500 mt-1">{description}</p>
      </div>
    </div>
  );

  if (to) {
    return (
      <Link
        to={to}
        className="block bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:border-primary-300 hover:shadow-md transition-all"
      >
        {content}
      </Link>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      {content}
    </div>
  );
}

// =============================================================================
// Icons
// =============================================================================

function TemplateIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"
      />
    </svg>
  );
}

function ChevronRightIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  );
}

function BuildingIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
      />
    </svg>
  );
}

function BellIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
      />
    </svg>
  );
}

function ShieldIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
      />
    </svg>
  );
}

function PlugIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
      />
    </svg>
  );
}

function InfoIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function CreditCardIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
      />
    </svg>
  );
}
